/*
 * Odaline core JS (single IIFE, deferred, loaded after Dawn's constants.js, pubsub.js, global.js
 * and cart-drawer.js). Exposes window.Odaline for the feature scripts (odaline-pdp.js,
 * odaline-cart.js) and inline section scripts.
 *
 * Ground rules:
 * - No DOM mutation observers. Cart changes come from Dawn's pub/sub `cart-update` event and from a
 *   wrapper around the cart drawer's renderContents (Dawn replaces the drawer HTML on every
 *   add / quantity change, so callbacks run again after that re-render).
 * - Everything inside the cart drawer must already be correct from the Liquid render; JS here is
 *   only an enhancement layer and every onCartUpdate callback must be idempotent.
 *
 * API (window.Odaline):
 *   settings                     -> window.odlSettings (theme settings, amounts in cents)
 *   formatMoney(cents, opts)     -> "$89.00" using window.odlMoneyFormat ({ trimZeros: true } -> "$89")
 *   parseDate("YYYY-MM-DD")      -> local Date at 00:00 (null if invalid)
 *   isBusinessDay(date)          -> false on weekends and US carrier holidays
 *   addBusinessDays(date, n)     -> new Date n business days after date
 *   formatDate(date)             -> "Nov 12"
 *   deliveryRange(min?, max?)    -> { from, to, fromText, toText, text: "Nov 12 - Nov 21" (en dash) }
 *   xmasCutoff()                 -> { active, date, text, daysLeft }
 *   onCartUpdate(cb)             -> calls cb(event) after every cart change; returns unsubscribe()
 *   fetchCart()                  -> Promise resolving to the /cart.js object (or null)
 */
(function () {
  'use strict';

  var MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  var DAY_MS = 86400000;

  var settings = window.odlSettings || {};

  function toInt(value, fallback) {
    var n = parseInt(value, 10);
    return isNaN(n) ? fallback : n;
  }

  /* ------------------------------------------------------------------ money */

  function formatMoney(cents, opts) {
    var trimZeros = !!(opts && (opts === true || opts.trimZeros));
    if (typeof cents === 'string') cents = cents.indexOf('.') > -1 ? Math.round(parseFloat(cents) * 100) : cents;
    cents = Math.round(Number(cents)) || 0;

    var format = window.odlMoneyFormat || '${{amount}}';
    var match = format.match(/\{\{\s*(\w+)\s*\}\}/);
    var key = match ? match[1] : 'amount';

    var negative = cents < 0;
    cents = Math.abs(cents);

    function num(value, decimals, thousands, decimal) {
      var fixed = (value / 100).toFixed(decimals).split('.');
      var whole = fixed[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
      return fixed[1] ? whole + decimal + fixed[1] : whole;
    }

    var out;
    switch (key) {
      case 'amount_no_decimals':
        out = num(cents, 0, ',', '.');
        break;
      case 'amount_with_comma_separator':
        out = num(cents, 2, '.', ',');
        break;
      case 'amount_no_decimals_with_comma_separator':
        out = num(cents, 0, '.', ',');
        break;
      case 'amount_with_apostrophe_separator':
        out = num(cents, 2, "'", '.');
        break;
      case 'amount_no_decimals_with_space_separator':
        out = num(cents, 0, ' ', '');
        break;
      case 'amount_with_space_separator':
        out = num(cents, 2, ' ', ',');
        break;
      case 'amount_with_period_and_space_separator':
        out = num(cents, 2, ' ', '.');
        break;
      default:
        out = num(cents, 2, ',', '.');
    }
    if (trimZeros && cents % 100 === 0) out = out.replace(/[.,]00$/, '');
    out = match ? format.replace(match[0], out) : out;
    return negative ? '-' + out : out;
  }

  /* ------------------------------------------------------------------ dates */

  function startOfDay(date) {
    var d = date ? new Date(date.getTime()) : new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  }

  /* "2026-12-01" -> local midnight (new Date("2026-12-01") would be UTC, i.e. Nov 30 in the US). */
  function parseDate(value) {
    if (value instanceof Date) return isNaN(value.getTime()) ? null : startOfDay(value);
    var m = String(value || '').trim().match(/^(\d{4})-(\d{1,2})-(\d{1,2})$/);
    if (!m) return null;
    var d = new Date(toInt(m[1], 0), toInt(m[2], 1) - 1, toInt(m[3], 1));
    return isNaN(d.getTime()) ? null : d;
  }

  function dateKey(d) {
    return d.getFullYear() + '-' + (d.getMonth() + 1) + '-' + d.getDate();
  }

  /* nth weekday of a month (weekday 0 = Sunday); n = -1 for the last one */
  function nthWeekday(year, month, weekday, n) {
    var d;
    if (n > 0) {
      d = new Date(year, month, 1);
      d.setDate(1 + ((weekday - d.getDay() + 7) % 7) + (n - 1) * 7);
    } else {
      d = new Date(year, month + 1, 0);
      d.setDate(d.getDate() - ((d.getDay() - weekday + 7) % 7));
    }
    return d;
  }

  /* Fixed-date holidays move to Friday / Monday when they fall on a weekend. */
  function observed(year, month, day) {
    var d = new Date(year, month, day);
    if (d.getDay() === 6) d.setDate(d.getDate() - 1);
    if (d.getDay() === 0) d.setDate(d.getDate() + 1);
    return d;
  }

  var holidayCache = {};

  /* US holidays on which the major carriers do not deliver. */
  function holidaysFor(year) {
    if (holidayCache[year]) return holidayCache[year];
    var map = {};
    [
      observed(year, 0, 1), // New Year's Day
      nthWeekday(year, 4, 1, -1), // Memorial Day
      observed(year, 6, 4), // Independence Day
      nthWeekday(year, 8, 1, 1), // Labor Day
      nthWeekday(year, 10, 4, 4), // Thanksgiving
      observed(year, 11, 25), // Christmas Day
    ].forEach(function (d) {
      map[dateKey(d)] = true;
    });
    // New Year's Day of the next year can be observed on Dec 31 of this one
    var nextNewYear = observed(year + 1, 0, 1);
    if (nextNewYear.getFullYear() === year) map[dateKey(nextNewYear)] = true;
    holidayCache[year] = map;
    return map;
  }

  function isBusinessDay(date) {
    var day = date.getDay();
    if (day === 0 || day === 6) return false;
    return !holidaysFor(date.getFullYear())[dateKey(date)];
  }

  function addBusinessDays(date, n) {
    var d = startOfDay(date instanceof Date ? date : parseDate(date) || new Date());
    var remaining = Math.max(0, toInt(n, 0));
    var guard = 0;
    while (remaining > 0 && guard < 400) {
      d.setDate(d.getDate() + 1);
      if (isBusinessDay(d)) remaining -= 1;
      guard += 1;
    }
    return d;
  }

  function formatDate(date) {
    return MONTHS[date.getMonth()] + ' ' + date.getDate();
  }

  function deliveryRange(min, max, fromDate) {
    var lo = toInt(min, toInt(settings.deliveryMin, 8));
    var hi = toInt(max, toInt(settings.deliveryMax, 15));
    if (hi < lo) {
      var swap = hi;
      hi = lo;
      lo = swap;
    }
    var start = fromDate ? startOfDay(fromDate) : startOfDay();
    var from = addBusinessDays(start, lo);
    var to = addBusinessDays(start, hi);
    var fromText = formatDate(from);
    var toText = formatDate(to);
    return {
      from: from,
      to: to,
      fromText: fromText,
      toText: toText,
      text: fromText === toText ? fromText : fromText + ' – ' + toText,
    };
  }

  function xmasCutoff() {
    var date = parseDate(settings.xmasCutoffDate);
    var text = settings.xmasCutoffText || '';
    if (!date || !text) return { active: false, date: date, text: text, daysLeft: 0 };
    var today = startOfDay();
    var daysLeft = Math.round((date.getTime() - today.getTime()) / DAY_MS);
    return { active: daysLeft >= 0, date: date, text: text, daysLeft: Math.max(0, daysLeft) };
  }

  /* ------------------------------------------------------------------ cart */

  function cartUrl() {
    return (window.routes && window.routes.cart_url) || '/cart';
  }

  function fetchCart() {
    return fetch(cartUrl() + '.js', { headers: { Accept: 'application/json' }, credentials: 'same-origin' })
      .then(function (r) {
        return r.ok ? r.json() : null;
      })
      .catch(function () {
        return null;
      });
  }

  var listeners = [];
  var pendingEvent = null;
  var flushTimer = null;

  function flush() {
    flushTimer = null;
    var event = pendingEvent || {};
    pendingEvent = null;
    listeners.slice().forEach(function (cb) {
      try {
        cb(event);
      } catch (e) {
        console.error('[Odaline] cart update callback failed', e);
      }
    });
  }

  /* Coalesces bursts (pub/sub + drawer re-render of the same add) into one callback run, on a
     macrotask so Dawn's synchronous innerHTML replacement has already happened. */
  function queueCartEvent(event) {
    pendingEvent = Object.assign({}, pendingEvent || {}, event || {});
    if (flushTimer) return;
    flushTimer = window.setTimeout(flush, 0);
  }

  function onCartUpdate(cb) {
    if (typeof cb !== 'function') return function () {};
    listeners.push(cb);
    return function unsubscribe() {
      listeners = listeners.filter(function (fn) {
        return fn !== cb;
      });
    };
  }

  function hookPubSub() {
    var events = typeof PUB_SUB_EVENTS !== 'undefined' ? PUB_SUB_EVENTS : null; // eslint-disable-line no-undef
    var sub = typeof window.subscribe === 'function' ? window.subscribe : typeof subscribe === 'function' ? subscribe : null; // eslint-disable-line no-undef
    if (!events || !sub) return;
    sub(events.cartUpdate, function (data) {
      queueCartEvent({
        source: (data && data.source) || 'cart-update',
        cartData: data && data.cartData,
        rendered: false,
      });
    });
  }

  /* Dawn calls renderContents right after publishing (and later still for quick-add modals), so
     listeners are re-run once the new drawer HTML is in place. */
  function wrapRenderContents(tagName) {
    var Ctor = window.customElements && window.customElements.get(tagName);
    if (!Ctor || !Ctor.prototype || typeof Ctor.prototype.renderContents !== 'function') return;
    if (Ctor.prototype.renderContents.__odlWrapped) return;
    var original = Ctor.prototype.renderContents;
    var wrapped = function (parsedState) {
      var result = original.apply(this, arguments);
      queueCartEvent({ source: tagName, rendered: true, cartData: parsedState });
      return result;
    };
    wrapped.__odlWrapped = true;
    Ctor.prototype.renderContents = wrapped;
  }

  function hookCartRenders() {
    wrapRenderContents('cart-drawer');
    wrapRenderContents('cart-notification');
    if (window.customElements && typeof window.customElements.whenDefined === 'function') {
      ['cart-drawer', 'cart-notification'].forEach(function (tag) {
        if (!window.customElements.get(tag)) {
          window.customElements.whenDefined(tag).then(function () {
            wrapRenderContents(tag);
          });
        }
      });
    }
  }

  hookPubSub();
  hookCartRenders();

  /* Back/forward cache restores a stale page: let listeners refresh from /cart.js. */
  window.addEventListener('pageshow', function (event) {
    if (event.persisted) queueCartEvent({ source: 'pageshow', rendered: false, cartData: null });
  });

  window.Odaline = {
    version: '1.0.0',
    settings: settings,
    formatMoney: formatMoney,
    parseDate: parseDate,
    isBusinessDay: isBusinessDay,
    addBusinessDays: addBusinessDays,
    formatDate: formatDate,
    deliveryRange: deliveryRange,
    xmasCutoff: xmasCutoff,
    onCartUpdate: onCartUpdate,
    fetchCart: fetchCart,
  };
})();
